import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  CataloguesFilterParams,
  CatalogueType,
  CataloguesListResponse,
  CreateCatalogueParams,
  UpdateCatalogueParams
} from '@/types/catalogue'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import {
  fetchCatalogues,
  fetchCatalogue,
  createCatalogue,
  updateCatalogue,
  deleteCatalogue,
  bulkDeleteCatalogues,
  sortCatalogue
} from '@/lib/api'

type CatalogueStateType = {
  data: CatalogueType | null
  isLoading: boolean
  error: string | null
}

type CataloguesListStateType = {
  data: CataloguesListResponse | null
  isLoading: boolean
  error: string | null
}

type CataloguesStoreState = {
  catalogue: CatalogueStateType
  cataloguesList: CataloguesListStateType
  filters: CataloguesFilterParams
  selectedCatalogueIds: string[]
  setFilters: (filters: Partial<CataloguesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchCatalogues: (params?: CataloguesFilterParams) => Promise<CataloguesListResponse>
  resetCataloguesList: () => void

  // Sort catalogue
  sortCatalogue: (id: string, type: SortOrderEnum) => Promise<CatalogueType>

  // Catalogue operations
  getCatalogue: (id: string) => Promise<CatalogueType>
  createCatalogue: (params: CreateCatalogueParams) => Promise<CatalogueType>
  updateCatalogue: (id: string, params: UpdateCatalogueParams) => Promise<CatalogueType>
  deleteCatalogue: (id: string) => Promise<void>
  bulkDeleteCatalogues: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectCatalogue: (id: string) => void
  deselectCatalogue: (id: string) => void
  toggleCatalogueSelection: (id: string) => void
  selectAllCatalogues: (catalogues: CatalogueType[]) => void
  deselectAllCatalogues: () => void
  isCatalogueSelected: (id: string) => boolean
  resetCatalogue: () => void
}

const defaultFilters: CataloguesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'order',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const initialCatalogueState: CatalogueStateType = {
  data: null,
  isLoading: false,
  error: null
}

const initialCataloguesListState: CataloguesListStateType = {
  data: null,
  isLoading: false,
  error: null
}

const useCataloguesStore = create<CataloguesStoreState>()(
  devtools((set, get) => ({
    catalogue: initialCatalogueState,
    cataloguesList: initialCataloguesListState,
    selectedCatalogueIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof CataloguesFilterParams]
          } else {
            updatedFilters[key as keyof CataloguesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: CataloguesFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as CataloguesFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof CataloguesFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getCatalogue: async id => {
      set(state => ({
        catalogue: {
          ...state.catalogue,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchCatalogue(id)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createCatalogue: async params => {
      set(state => ({
        catalogue: {
          ...state.catalogue,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createCatalogue(params)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateCatalogue: async (id, params) => {
      set(state => ({
        catalogue: {
          ...state.catalogue,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateCatalogue(id, params)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteCatalogue: async id => {
      set(state => ({
        catalogue: {
          ...state.catalogue,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteCatalogue(id)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetCatalogue: () => {
      set(state => ({
        catalogue: {
          ...state.catalogue,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectCatalogue: id => {
      set(state => {
        if (state.selectedCatalogueIds.includes(id)) {
          return state
        }
        return { selectedCatalogueIds: [...state.selectedCatalogueIds, id] }
      })
    },

    deselectCatalogue: id => {
      set(state => ({
        selectedCatalogueIds: state.selectedCatalogueIds.filter(catalogueId => catalogueId !== id)
      }))
    },

    toggleCatalogueSelection: id => {
      set(state => {
        if (state.selectedCatalogueIds.includes(id)) {
          return {
            selectedCatalogueIds: state.selectedCatalogueIds.filter(catalogueId => catalogueId !== id)
          }
        }
        return { selectedCatalogueIds: [...state.selectedCatalogueIds, id] }
      })
    },

    selectAllCatalogues: catalogues => {
      set(() => ({
        selectedCatalogueIds: catalogues.map(catalogue => catalogue.id)
      }))
    },

    deselectAllCatalogues: () => {
      set(() => ({ selectedCatalogueIds: [] }))
    },

    isCatalogueSelected: id => {
      return get().selectedCatalogueIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteCatalogues: async ids => {
      set(state => ({
        catalogue: {
          ...state.catalogue,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteCatalogues({ ids })

        set(state => ({
          catalogue: {
            ...state.catalogue,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedCatalogueIds: state.selectedCatalogueIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          catalogue: {
            ...state.catalogue,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchCatalogues: async (params?: CataloguesFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        cataloguesList: {
          ...state.cataloguesList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchCatalogues(filtersToUse)

        set(state => ({
          cataloguesList: {
            ...state.cataloguesList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          cataloguesList: {
            ...state.cataloguesList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetCataloguesList: () => {
      set(state => ({
        cataloguesList: {
          ...state.cataloguesList,
          data: null,
          error: null
        }
      }))
    },

    sortCatalogue: async (id, type) => {
      set(() => ({
        catalogue: {
          ...get().catalogue,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await sortCatalogue(id, type)

        set(() => ({
          catalogue: {
            ...get().catalogue,
            isLoading: false,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          catalogue: {
            ...initialCatalogueState,
            error: getErrorMessage(error)
          }
        }))
        throw error
      }
    }
  }))
)

export default useCataloguesStore
